import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Search, FileText, Package, Store, Compass, CornerDownLeft, X } from 'lucide-react'
import styles from './SearchOverlay.module.css'
import Skeleton from './ui/Skeleton'
import { buildSearchIndex, searchResults } from '../lib/search'
import { useI18n } from '../lib/i18n'

const LIST_ID = 'search-overlay-results'

const groupIcon = {
  Pages: Compass,
  Products: Package,
  'Help & Blog': FileText,
  Marketplace: Store,
}

const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState(null)
  const [active, setActive] = useState(0)
  const inputRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    let activeFlag = true
    if (open) {
      buildSearchIndex().then((idx) => { if (activeFlag) setIndex(idx) })
    } else {
      setQuery('')
      setIndex(null)
      setActive(0)
    }
    return () => { activeFlag = false }
  }, [open])

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 40)
      const onKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          onClose()
        }
      }
      window.addEventListener('keydown', onKey)
      return () => {
        clearTimeout(t)
        window.removeEventListener('keydown', onKey)
      }
    }
  }, [open, onClose])

  const results = useMemo(() => (index ? searchResults(index, query) : []), [index, query])
  const grouped = useMemo(() => {
    const map = new Map()
    for (const r of results) {
      if (!map.has(r.group)) map.set(r.group, [])
      map.get(r.group).push(r)
    }
    return [...map.entries()]
  }, [results])

  useEffect(() => { setActive(0) }, [query])

  const go = useCallback((item) => {
    onClose()
    navigate(item.href)
  }, [navigate, onClose])

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) go(results[active]) }
    else if (e.key === 'Escape') { e.preventDefault(); onClose() }
    else if (e.key === 'Tab') {
      // Keep the keyboard inside the modal dialog.
      const focusables = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
  }

  if (!open) return null

  const activeId = results[active] ? `${LIST_ID}-opt-${active}` : undefined

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Search">
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.inputWrap}>
          <Search className={styles.icon} size={20} />
          <input
            ref={inputRef}
            className={styles.input}
            placeholder={t('common.search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls={LIST_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
          />
          {query ? (
            <button className={styles.clearBtn} onClick={() => setQuery('')} aria-label="Clear search">
              <X size={16} />
            </button>
          ) : (
            <kbd className={styles.kbd}>ESC</kbd>
          )}
        </div>

        <div className={styles.body}>
          {!index && (
            <Skeleton rows={3} label="Loading results" />
          )}

          {index && !query && (
            <div className={styles.empty}>
              <Compass size={20} />
              <p>Start typing to search across the site.</p>
              <p className={styles.tip}>Use <kbd>↑</kbd> <kbd>↓</kbd> to navigate, <kbd>Enter</kbd> to open.</p>
            </div>
          )}

          {index && query && results.length === 0 && (
            <div className={styles.empty}>
              <Search size={20} />
              <p>No results for “{query}”.</p>
            </div>
          )}

          {query && results.length > 0 && (
            <ul className={styles.list} role="listbox" id={LIST_ID} aria-label="Search results">
              {grouped.map(([group, items]) => {
                const Icon = groupIcon[group] || Compass
                return (
                  <li key={group} className={styles.group} role="group" aria-label={group}>
                    <div className={styles.groupLabel}>
                      <Icon size={13} /> {group}
                    </div>
                    <ul className={styles.groupList} role="none">
                      {items.map((item, i) => {
                        const globalIndex = results.indexOf(item)
                        const isActive = globalIndex === active
                        return (
                          <li key={`${item.type}-${item.href}-${i}`} role="none">
                            <button
                              id={`${LIST_ID}-opt-${globalIndex}`}
                              className={`${styles.result} ${isActive ? styles.resultActive : ''}`}
                              role="option"
                              aria-selected={isActive}
                              onMouseEnter={() => setActive(globalIndex)}
                              onClick={() => go(item)}
                            >
                              <Icon className={styles.resultIcon} size={16} />
                              <span className={styles.resultTitle}>{item.title}</span>
                              {item.subtitle && <span className={styles.resultSub}>{item.subtitle}</span>}
                              <span className={styles.resultGo}><CornerDownLeft size={14} /></span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>esc</kbd> Close</span>
          {query && results.length > 0 && (
            <span className={styles.footerCount}>{results.length} results</span>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default SearchOverlay